/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useTranslations } from 'next-intl'
import React, { useEffect } from 'react'

import { Button } from '@/components/ui/Button'
import { Link } from '@/i18n/navigation'

/** A description of the available properties for this error page. */
type EcommerceErrorPageProps = {
  /** The error that triggered this page. */
  error: Error & { digest?: string }

  /** Reset handler to trigger a re-render of the failed segment. */
  reset: () => void
}

/**
 * Generic error page for uncaught errors on the e-commerce website.
 */
const EcommerceErrorPage: React.FC<EcommerceErrorPageProps> = ({ error, reset }) => {
  const t = useTranslations('EcommerceErrorPage')

  useEffect(() => {
    // Sentry.captureException(error);
  }, [error])

  return (
    <main className={'grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8'}>
      <div className={'text-center'}>
        <p className={'text-base font-semibold text-primary-500'}>{t('whoops')}</p>
        <h1 className={'mt-4 text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-7xl'}>
          {t('somethingWentWrong')}
        </h1>
        <p className={'mt-6 text-pretty text-lg font-medium text-gray-500 sm:text-xl/8'}>{t('embarrassingMessage')}</p>
        <p className={'mt-6 text-pretty text-sm font-medium text-gray-500 sm:text-md'}>{t('tryAgainMessage')}</p>
        <div className={'mt-10 flex items-center justify-center gap-x-6'}>
          <Button variant={'primary'} onClick={() => reset()}>
            {t('tryAgain')}
          </Button>
          <Link href={'/'} className={'text-sm font-semibold text-gray-900'}>
            {t('goBackHome')} <span aria-hidden={'true'}>&rarr;</span>
          </Link>
        </div>
      </div>
    </main>
  )
}

export default EcommerceErrorPage
